import React, { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ChevronRight } from 'lucide-react';

const slides = [
  {
    emoji: "📅",
    title: "Never Forget",
    subtitle: "What Matters",
    body: "Birthdays, anniversaries, date nights — we'll remind you both.",
  },
  {
    emoji: "💭",
    title: "Know How",
    subtitle: "They Feel",
    body: "One minute a day to truly understand each other.",
  },
  {
    emoji: "🤝",
    title: "Resolve",
    subtitle: "Together",
    body: "Every disagreement solved in 5 minutes or less.",
  }
];

const Onboarding = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const containerRef = useRef(null);
  const navigate = useNavigate();
  
  // Minimum swipe distance to trigger slide change (in pixels)
  const minSwipeDistance = 50;
  
  const handleNext = () => {
    if (currentSlide < slides.length - 1) {
      setCurrentSlide(currentSlide + 1);
    } else {
      navigate('/login');
    }
  };
  
  const goToSlide = (index) => {
    setCurrentSlide(index);
  };
  
  const onTouchStart = useCallback((e) => {
    setTouchStart(e.targetTouches[0].clientX);
    setIsDragging(true);
  }, []);
  
  const onTouchMove = useCallback((e) => {
    if (!touchStart || !isDragging) return;
    const currentTouch = e.targetTouches[0].clientX;
    const diff = currentTouch - touchStart;
    
    // Add resistance at edges
    let adjustedDiff = diff;
    if ((currentSlide === 0 && diff > 0) || (currentSlide === slides.length - 1 && diff < 0)) {
      adjustedDiff = diff * 0.3; // Rubber band effect at edges
    }
    
    setDragOffset(adjustedDiff);
  }, [touchStart, isDragging, currentSlide]);
  
  const onTouchEnd = useCallback(() => {
    if (!isDragging) return;
    
    const threshold = minSwipeDistance;
    
    if (dragOffset < -threshold && currentSlide < slides.length - 1) {
      // Swipe left = go to next slide
      setCurrentSlide(prev => prev + 1);
    } else if (dragOffset > threshold && currentSlide > 0) {
      // Swipe right = go to previous slide
      setCurrentSlide(prev => prev - 1);
    }
    
    setIsDragging(false);
    setDragOffset(0);
    setTouchStart(null);
  }, [dragOffset, currentSlide, isDragging]);
  
  const isLastSlide = currentSlide === slides.length - 1;
  
  return (
    <div className="h-[100dvh] flex flex-col bg-white overflow-hidden">
      {/* Logo - large, matching emoji size (~100px visual) */}
      <div className="flex justify-center pt-6 px-6">
        <img 
          src="/couplein_logo.png"
          alt="CoupleIn"
          className="h-36 w-auto"
        />
      </div>
      
      {/* Swipeable carousel container */}
      <div 
        ref={containerRef}
        className="flex-1 overflow-hidden touch-pan-y select-none -mt-8 relative"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {/* Slides wrapper - horizontal strip using flexbox */}
        <div 
          className="h-full flex will-change-transform"
          style={{
            transform: `translateX(calc(-${currentSlide * 100}% + ${dragOffset}px))`,
            transition: isDragging ? 'none' : 'transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
          }}
        >
          {slides.map((slide, index) => (
            <div 
              key={index}
              className="h-full flex flex-col items-center justify-center px-6 flex-shrink-0 w-full"
            >
              {/* Emoji */}
              <div className="text-8xl mb-6">
                {slide.emoji}
              </div>
              
              {/* Title */}
              <h1 className="text-4xl font-bold text-gray-900 text-center leading-tight">
                {slide.title}
              </h1>
              <h1 className="text-4xl font-bold text-gradient text-center leading-tight">
                {slide.subtitle}
              </h1>
              
              {/* Body */}
              <p className="text-lg text-gray-500 text-center mt-3 max-w-xs">
                {slide.body}
              </p>
              
              {/* Swipe hint - only show on first slide when viewing first slide */}
              {index === 0 && currentSlide === 0 && (
                <p className="text-sm text-gray-300 mt-6 animate-pulse">
                  Swipe to explore →
                </p>
              )}
            </div>
          ))}
        </div>
      </div>
      
      {/* Bottom section */}
      <div className="pb-8 px-6 safe-bottom">
        {/* Progress dots */}
        <div className="flex justify-center gap-2 mb-5">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`h-2 rounded-full transition-all duration-300 tap-feedback ${
                index === currentSlide 
                  ? 'w-8 bg-primary' 
                  : 'w-2 bg-gray-200 hover:bg-gray-300'
              }`}
            />
          ))}
        </div>
        
        {/* Button */}
        <Button
          onClick={handleNext}
          data-testid="onboarding-next-btn"
          className="w-full h-14 text-lg rounded-2xl gradient-brand shadow-pink text-white font-semibold"
        >
          {isLastSlide ? "Get Started" : "Continue"}
          <ChevronRight className="ml-2 h-5 w-5" />
        </Button>
      </div>
    </div>
  );
};

export default Onboarding;
